import { useCallback, useMemo } from 'react';
import { ButtonVariants, Form, FormSizes, Modal, ModalSizes } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { useCreateTerminalMutation, useUpdateTerminalMutation } from '../../api/sitesApi';
import { SITE_FIELDS } from '../../constants/sites.constants';
import { buildTerminalDto, toTerminalModalValues } from '../../mappers/sitesMappers';
import { TerminalModalFields } from './TerminalModalFields';
import styles from './TerminalModal.module.scss';

/**
 * Props:
 *   open       whether the modal is shown
 *   onClose    close handler (✕ / Cancel)
 *   siteId     the site the terminals belong to
 *   siteName   shown in the modal title
 *   terminal   the row being edited; omit to add new terminals
 *   freeSlots  how many terminals the site can still take — add mode only
 */
export const TerminalModal = ({ open, onClose, siteId, siteName, terminal, freeSlots = 1 }) => {
  const t = useTranslations();
  const [createTerminal, { isLoading: isCreating }] = useCreateTerminalMutation();
  const [updateTerminal, { isLoading: isUpdating }] = useUpdateTerminalMutation();

  const isEdit = Boolean(terminal);
  const initialValues = useMemo(() => toTerminalModalValues(terminal), [terminal]);

  const handleSubmit = useCallback(
    async (values) => {
      const terminalKeys = values[SITE_FIELDS.terminalKeys] ?? [];

      for (const terminalKey of terminalKeys) {
        const body = buildTerminalDto(values, terminalKey, siteId);
        const result = isEdit
          ? await updateTerminal({ id: terminal.id, ...body })
          : await createTerminal(body);
        if (result.error) return;
      }

      onClose();
    },
    [isEdit, terminal, siteId, createTerminal, updateTerminal, onClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={
            isEdit
              ? t('SITES_TERMINAL_EDIT_TITLE', { siteName })
              : t('SITES_TERMINAL_ADD_TITLE', { siteName })
          }
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: t('COMMON_SAVE'),
              variant: ButtonVariants.primary,
              disabled: isCreating || isUpdating,
              onClick: submit,
            },
          ]}
        >
          <TerminalModalFields maxTerminals={isEdit ? 1 : freeSlots} canAddMore={!isEdit} />
        </Modal>
      )}
    </Form>
  );
};
